// FAQ section — two-column layout: left = category nav, right = questions + accordion.
// Uses Cognidoor landing blues and Framer Motion for smooth, fluid transitions.

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import {
  FAQ_SECTIONS,
  getItemsBySection,
  getFeaturedItems,
  getItemById,
  type FaqItem,
} from "../../content/faqCatalog";

const cx = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(" ");

/** Same gradient as hero (all track): 135deg blue → cyan → green → blue */
const HERO_GRADIENT =
  "linear-gradient(135deg, var(--cog-blue-1, #277AB2) 0%, #0891b2 30%, #34d399 60%, var(--cog-blue-2, #0B5996) 100%)";

const TAB_ITEMS = [
  { id: "highlights", label: "Highlights" },
  ...FAQ_SECTIONS.map((s) => ({ id: s.id, label: s.title })),
];

const sectionVariants = {
  enter: { opacity: 0, y: 12 },
  center: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
};

const itemVariants = {
  enter: (i: number) => ({ opacity: 0, y: 16 }),
  center: (i: number) => ({ opacity: 1, y: 0 }),
  exit: (i: number) => ({ opacity: 0, y: -8 }),
};

export default function FaqSection() {
  const [activeSection, setActiveSection] = useState<string>("highlights");
  const [openId, setOpenId] = useState<string | null>(null);

  const hashToId = (hash: string) => {
    if (!hash || !hash.startsWith("#faq-")) return null;
    return hash.slice("#faq-".length);
  };

  const applyHash = useCallback((id: string | null) => {
    if (typeof window === "undefined") return;
    const newHash = id ? `#faq-${id}` : "#faq";
    if (window.location.hash !== newHash) {
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${newHash}`);
    }
  }, []);

  useEffect(() => {
    const onHashChange = () => {
      const id = hashToId(window.location.hash);
      if (id && getItemById(id)) {
        setOpenId(id);
        const section = getItemById(id)?.section;
        if (section) setActiveSection(section);
      }
    };
    onHashChange();
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  useEffect(() => {
    if (!openId) return;
    const el = document.getElementById(`faq-${openId}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [openId]);

  const handleOpen = (id: string) => {
    setOpenId((prev) => (prev === id ? null : id));
    applyHash(openId === id ? null : id);
  };

  const displayItems: FaqItem[] = useMemo(() => {
    if (activeSection === "highlights") return getFeaturedItems();
    return getItemsBySection(activeSection);
  }, [activeSection]);

  return (
    <section id="faq" className="relative w-full overflow-hidden text-left">
      {/* Full-width hero-style background — slightly lighter so section is brighter */}
      <div
        className="absolute inset-0 opacity-85"
        style={{ background: HERO_GRADIENT }}
        aria-hidden
      />
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 20% 10%, rgba(255,255,255,0.25) 0%, transparent 45%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.18) 0%, transparent 45%), radial-gradient(circle at 50% 95%, rgba(255,255,255,0.1) 0%, transparent 45%)",
        }}
        aria-hidden
      />
      {/* Dot matrix — same as hero */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.10] [background-image:radial-gradient(circle_at_1px_1px,white_1px,transparent_0)] [background-size:22px_22px]"
        aria-hidden
      />
      {/* Light overlay so content stays readable; slightly brighter */}
      <div className="absolute inset-0 bg-white/78" aria-hidden />

      <div className="relative w-full px-4 sm:px-8 md:px-16 py-16 md:py-20 lg:py-24 flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full max-w-7xl">
          {/* Heading above the row — on lg, aligned with question column (spacer matches nav width + gap) */}
          <div className="mb-10 md:mb-12 flex flex-col lg:flex-row lg:gap-12">
            <div className="hidden lg:block lg:w-72 shrink-0" aria-hidden />
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.4 }}
              className="flex-1 min-w-0 max-w-5xl text-center"
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2 tracking-tight">
                Frequently asked questions
              </h2>
              <p className="text-slate-600 text-lg max-w-xl mx-auto">
                Quick answers about Cognidoor, plans, coaching, and billing. Can’t find what you need? Get in touch below.
              </p>
            </motion.div>
          </div>

          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 justify-center items-center lg:items-start">
            {/* Left column: category nav — 4×2 grid on mobile (full width to match content), vertical list on lg */}
            <nav
              className="grid grid-cols-4 lg:flex lg:flex-col gap-2 lg:gap-2 lg:w-72 shrink-0 w-full lg:max-w-none lg:justify-start"
              aria-label="FAQ categories"
            >
            {TAB_ITEMS.map((tab) => {
              const isActive = activeSection === tab.id;
              return (
                <motion.button
                  key={tab.id}
                  onClick={() => setActiveSection(tab.id)}
                  className={cx(
                    "w-full lg:w-full text-left px-2 py-2.5 lg:px-5 lg:py-4 rounded-lg lg:rounded-xl text-xs lg:text-base font-medium transition-all duration-200",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500",
                    isActive
                      ? "bg-gradient-to-r from-blue-600 to-blue-400 text-white shadow-md"
                      : "bg-white/80 text-slate-600 border border-slate-200/80 hover:border-blue-300 hover:bg-blue-50/50"
                  )}
                  whileHover={
                    isActive
                      ? { scale: 1.01, boxShadow: "0 6px 16px rgba(37, 99, 235, 0.4)" }
                      : { scale: 1.01 }
                  }
                  whileTap={{ scale: 0.99 }}
                >
                  {tab.label}
                </motion.button>
              );
            })}
          </nav>

          {/* Right column: questions + CTA — top-aligned with nav, content centered in max-w-5xl */}
          <div className="flex-1 min-w-0 w-full flex flex-col items-center">
            <div className="w-full max-w-5xl">
            <div className="w-full">
            <AnimatePresence mode="wait">
              {displayItems.length === 0 ? (
                <motion.p
                  key="empty"
                  variants={sectionVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.25 }}
                  className="text-slate-500 py-12 text-center text-lg"
                >
                  No questions in this category.
                </motion.p>
              ) : (
                <motion.div
                  key={activeSection}
                  variants={sectionVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.3 }}
                  className="space-y-5"
                >
                  {displayItems.map((item, i) => {
                    const isOpen = openId === item.id;
                    return (
                      <motion.div
                        key={item.id}
                        id={`faq-${item.id}`}
                        layout
                        variants={itemVariants}
                        custom={i}
                        initial="enter"
                        animate="center"
                        transition={{ delay: i * 0.04, duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                        className={cx(
                          "rounded-2xl border-2 overflow-hidden",
                          "transition-all duration-300 ease-out",
                          isOpen
                            ? "border-blue-400/70 bg-gradient-to-b from-blue-50/90 to-white shadow-lg shadow-blue-200/30 ring-2 ring-blue-300/20"
                            : "border-slate-200/80 bg-white/90 shadow-sm hover:border-slate-300 hover:shadow-md hover:bg-slate-50/50"
                        )}
                      >
                        <button
                          onClick={() => handleOpen(item.id)}
                          className={cx(
                            "w-full flex items-center justify-between gap-4 px-6 py-5 text-left transition-colors duration-200",
                            isOpen && "bg-blue-50/40"
                          )}
                          aria-expanded={isOpen}
                          aria-controls={`faq-answer-${item.id}`}
                          id={`faq-question-${item.id}`}
                        >
                          <span className={cx(
                            "font-semibold pr-2 text-lg md:text-xl",
                            isOpen ? "text-slate-900" : "text-slate-800"
                          )}>
                            {item.question}
                          </span>
                          <motion.span
                            animate={{ rotate: isOpen ? 180 : 0 }}
                            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                          >
                            <ChevronDownIcon className={cx("w-6 h-6 shrink-0 transition-colors", isOpen ? "text-blue-500" : "text-slate-400")} aria-hidden />
                          </motion.span>
                        </button>
                        <AnimatePresence initial={false}>
                          {isOpen && (
                            <motion.div
                              id={`faq-answer-${item.id}`}
                              role="region"
                              aria-labelledby={`faq-question-${item.id}`}
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                              className="overflow-hidden"
                            >
                              <div
                                className="px-6 pb-6 pt-1 text-slate-600 text-lg leading-relaxed border-t border-blue-100/80 bg-gradient-to-b from-blue-50/50 to-transparent"
                              >
                                {item.answer}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
            </div>

            {/* CTA */}
            <motion.div
              layout
              className="mt-12 pt-10 border-t border-slate-200/80 text-center w-full"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              <p className="text-slate-600 mb-4 text-lg">Still have a question?</p>
              <motion.a
                href="mailto:cogni.dore@gmail.com"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 text-white rounded-lg bg-gradient-to-r from-blue-600 to-blue-400 font-semibold hover:shadow-md transition-all duration-200"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Contact support
              </motion.a>
            </motion.div>
          </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </section>
  );
}
